import React, {
  useMemo,
  useState,
  useContext,
  useRef,
  useLayoutEffect,
  useEffect,
  useCallback
} from 'react';
import classNames from 'classnames';
import {
  OverlayScrollbarsComponent,
  OverlayScrollbarsComponentRef
} from 'overlayscrollbars-react';
import Transparent from '@assets/images/chroma-key.png';
import type { MediaViewerMobileActionVisibility } from '@pages/MediaViewer/components/MediaViewerMobileAction/MediaViewerMobileAction';
import { AssetVersionItemDto, ImageMetadataDto } from '@api/Api';
import PanelAction from '@pages/MediaViewer/AssetViewer/PanelAction';
import PlaybackPreparingLoader, {
  DELAYED_LOADER_MS,
  PREVIOUS_MEDIA_LOADER_DELAY_MS
} from '@pages/MediaViewer/AssetViewer/PlaybackPreparingLoader';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import { FabricContext } from '@context/FabricContext';
import FabricCanvas from '@pages/MediaViewer/AssetViewer/Canvas/Canvas';
import type { AssetViewerZoomOption } from '@context/AssetViewerStateContext/types';
import { useResponsive } from '@hooks/useResponsive';
import {
  ImagePinchGestureState,
  useMobileImagePinchZoom
} from '@pages/MediaViewer/AssetViewer/ImageCard/hooks/useMobileImagePinchZoom';
import { isDrawToolInUse } from '@pages/MediaViewer/helpers/drawToolShape';
import { getAssetAnnotationZoom } from '@pages/MediaViewer/AssetViewer/Canvas/utils/annotationCoordinates';
import {
  getScrollLayoutMargin,
  layoutAnnotationCanvas
} from '@pages/MediaViewer/AssetViewer/Canvas/utils/annotationLayout';
import {
  LastShownImage,
  setLastShownMedia,
  takeLastShownMedia
} from '@pages/MediaViewer/AssetViewer/lastShownMedia';

import './ImageCard.less';

interface IImageCardProps {
  isExpand: boolean;
  setIsExpand(val: boolean): void;
  onReady: () => void;
  asset: AssetVersionItemDto;
  isPreparing: boolean;
  isAssetReady: boolean;
  mobileActionVisibility?: MediaViewerMobileActionVisibility | null;
}

type Size = { width: number; height: number };

// Ctrl + wheel (or a touchpad pinch) zooms at the cursor, when it just happened
const WHEEL_ZOOM_FOCAL_MAX_AGE_MS = 300;

/** The image's box on screen: rotated by a multiple of 90°, zoomed */
function getImageBoxSize(size: Size, turnoverDegree: number, zoom: number) {
  const isSideways = turnoverDegree % 180 !== 0;
  return {
    width: (isSideways ? size.height : size.width) * zoom,
    height: (isSideways ? size.width : size.height) * zoom
  };
}

function getImageTransform(turnoverDegree: number, zoom: number) {
  return `translateX(-50%) translateY(-50%) rotate(${turnoverDegree}deg) scale(${zoom})`;
}

function ImageCard({
  isExpand,
  setIsExpand,
  onReady,
  asset,
  isPreparing,
  isAssetReady,
  mobileActionVisibility
}: IImageCardProps) {
  const fabricContext = useContext(FabricContext);
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<OverlayScrollbarsComponentRef<'div'>>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const imagePinchGestureRef = useRef<ImagePinchGestureState>({
    isActive: false,
    startSpan: 0,
    baseZoom: 1,
    focalX: 0,
    focalY: 0
  });

  const {
    shapesDrawingTools,
    isSelectedCanvasObject,
    zoomFactor,
    zoomOption,
    turnoverDegree,
    setTurnoverDegree,
    setZoomFactor,
    setZoomOption,
    isMobileDrawingToolbarOpen,
    selectedCommentId
  } = useAssetViewerState();
  const { shape } = shapesDrawingTools;
  const { isMdWidth, isLgWidth } = useResponsive();
  const isShapeDrawing = isDrawToolInUse({
    shape,
    isMdWidth,
    isMobileDrawingToolbarOpen,
    selectedCommentId
  });

  const fileUrl = useMemo(() => {
    const { proxy } = asset.metadata as ImageMetadataDto;
    return proxy.url || '';
  }, [asset]);
  // Previous asset's image, until this one loads or the loader covers it.
  // Taken on mount, not on render: the previous viewer stores it when it
  // unmounts, which is in the same commit when switching right away.
  const [previousImage, setPreviousImage] = useState<LastShownImage | null>(
    null
  );
  const previousImageRef = useRef(previousImage);
  previousImageRef.current = previousImage;
  useLayoutEffect(() => {
    setPreviousImage(takeLastShownMedia('image'));
  }, []);
  // The previous image is kept longer than an empty frame would be
  const loaderDelayMs = previousImage
    ? PREVIOUS_MEDIA_LOADER_DELAY_MS
    : DELAYED_LOADER_MS;
  const [isSlowLoad, setIsSlowLoad] = useState(false);
  useEffect(() => {
    if (isAssetReady) return;
    const timeout = setTimeout(() => setIsSlowLoad(true), loaderDelayMs);
    return () => clearTimeout(timeout);
  }, [isAssetReady, loaderDelayMs]);
  const isPreviousImageShown =
    !!previousImage && !isAssetReady && !isPreparing && !isSlowLoad;

  const [imageSize, setImageSize] = useState({
    width: (asset.metadata as ImageMetadataDto).info.imageInfo?.width ?? 1280,
    height: (asset.metadata as ImageMetadataDto).info.imageInfo?.height ?? 720
  });

  // Viewport size, which the fit zoom options fit the image into. Changes when
  // the comments panel is opened or closed, or the viewer is expanded
  const [containerSize, setContainerSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // Layout effect: the viewport is measured before the image is first painted
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const measure = () => {
      const width = container.offsetWidth;
      const height = container.offsetHeight;
      setContainerSize((prev) =>
        prev && prev.width === width && prev.height === height
          ? prev
          : { width, height }
      );
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Zoom shown: the applied one, or the one being pinched to
  const displayedZoomRef = useRef(zoomFactor);
  const imageSizeRef = useRef(imageSize);
  imageSizeRef.current = imageSize;
  const turnoverDegreeRef = useRef(turnoverDegree);
  turnoverDegreeRef.current = turnoverDegree;

  const getScrollViewport = useCallback(
    () => wrapperRef.current?.osInstance()?.elements().viewport ?? null,
    []
  );

  // The annotation canvas covers the visible part of the image (and around
  // it, so scrolling a bit does not lay it out again), the frame outlines the
  // whole image. Both are in the scroll content: they scroll with the image.
  const syncCanvasLayout = useCallback(() => {
    const image = imageRef.current;
    const content = contentRef.current;
    const container = canvasContainerRef.current;
    const { canvas } = fabricContext;
    if (!image || !content || !container || !canvas) return;

    const contentRect = content.getBoundingClientRect();
    const origin = {
      left: contentRect.left + content.clientLeft,
      top: contentRect.top + content.clientTop
    };
    const toContent = (rect: DOMRect) => ({
      left: rect.left - origin.left,
      top: rect.top - origin.top,
      width: rect.width,
      height: rect.height
    });
    const asset = toContent(image.getBoundingClientRect());

    const frame = frameRef.current;
    if (frame) {
      frame.style.left = `${asset.left - frame.clientLeft}px`;
      frame.style.top = `${asset.top - frame.clientTop}px`;
      frame.style.width = `${asset.width}px`;
      frame.style.height = `${asset.height}px`;
    }

    const visibleRect = (
      getScrollViewport() ?? containerRef.current
    )?.getBoundingClientRect();
    if (!visibleRect) return;
    layoutAnnotationCanvas({
      canvas,
      element: container,
      asset,
      visible: toContent(visibleRect),
      zoom: getAssetAnnotationZoom(displayedZoomRef.current),
      margin: getScrollLayoutMargin(visibleRect),
      snapOrigin: origin
    });
  }, [fabricContext, getScrollViewport]);

  // How the image was scrolled, to keep a point of it in place when zooming
  const scrollStateRef = useRef<{
    zoom: number;
    // The image's center, in the scroll content
    centerX: number;
    centerY: number;
    scrollLeft: number;
    scrollTop: number;
  } | null>(null);
  // The point to keep in place on the next zoom change (client coordinates),
  // otherwise the middle of the view
  const zoomFocalRef = useRef<{ x: number; y: number; time: number } | null>(
    null
  );
  // Scrolled to the middle of the image on the next layout, e.g. for a fit
  // zoom option
  const centerScrollRef = useRef(false);

  const getImageCenter = useCallback((viewport: HTMLElement) => {
    const image = imageRef.current;
    if (!image) return null;
    const imageRect = image.getBoundingClientRect();
    const viewportRect = viewport.getBoundingClientRect();
    return {
      x:
        imageRect.left +
        imageRect.width / 2 -
        viewportRect.left -
        viewport.clientLeft +
        viewport.scrollLeft,
      y:
        imageRect.top +
        imageRect.height / 2 -
        viewportRect.top -
        viewport.clientTop +
        viewport.scrollTop
    };
  }, []);

  const rememberScrollState = useCallback(() => {
    const viewport = getScrollViewport();
    const center = viewport && getImageCenter(viewport);
    if (!viewport || !center) return;
    scrollStateRef.current = {
      zoom: displayedZoomRef.current,
      centerX: center.x,
      centerY: center.y,
      scrollLeft: viewport.scrollLeft,
      scrollTop: viewport.scrollTop
    };
  }, [getScrollViewport, getImageCenter]);

  // After the image is shown at another zoom: the point of the image at the
  // focal point stays there
  const scrollAfterZoom = useCallback(() => {
    const viewport = getScrollViewport();
    const center = viewport && getImageCenter(viewport);
    if (!viewport || !center) return;

    if (centerScrollRef.current) {
      centerScrollRef.current = false;
      zoomFocalRef.current = null;
      viewport.scrollLeft = (viewport.scrollWidth - viewport.clientWidth) / 2;
      viewport.scrollTop = (viewport.scrollHeight - viewport.clientHeight) / 2;
      return;
    }

    const previous = scrollStateRef.current;
    const zoom = displayedZoomRef.current;
    if (!previous || previous.zoom === zoom) return;

    const viewportRect = viewport.getBoundingClientRect();
    const focalPoint = zoomFocalRef.current;
    zoomFocalRef.current = null;
    const focal =
      focalPoint && Date.now() - focalPoint.time < WHEEL_ZOOM_FOCAL_MAX_AGE_MS
        ? {
            x: focalPoint.x - viewportRect.left - viewport.clientLeft,
            y: focalPoint.y - viewportRect.top - viewport.clientTop
          }
        : { x: viewport.clientWidth / 2, y: viewport.clientHeight / 2 };

    // The focal point, from the image's center in image pixels
    const imageX =
      (previous.scrollLeft + focal.x - previous.centerX) / previous.zoom;
    const imageY =
      (previous.scrollTop + focal.y - previous.centerY) / previous.zoom;
    viewport.scrollLeft = center.x + imageX * zoom - focal.x;
    viewport.scrollTop = center.y + imageY * zoom - focal.y;
  }, [getScrollViewport, getImageCenter]);

  const onScroll = useCallback(() => {
    syncCanvasLayout();
    rememberScrollState();
  }, [syncCanvasLayout, rememberScrollState]);

  const scrollEvents = useMemo(
    () => ({
      initialized: () => {
        scrollAfterZoom();
        syncCanvasLayout();
        rememberScrollState();
      },
      scroll: onScroll
    }),
    [scrollAfterZoom, syncCanvasLayout, rememberScrollState, onScroll]
  );

  const getZoomFactorFromOption = useCallback(
    (option: NonNullable<AssetViewerZoomOption>) => {
      if (option === 'actual-size') return 1;
      if (!containerSize || !imageRef.current) return 1;
      const width =
        Math.abs(imageSize.width * Math.cos(turnoverDegree * (Math.PI / 180))) +
        Math.abs(imageSize.height * Math.sin(turnoverDegree * (Math.PI / 180)));
      const height =
        Math.abs(imageSize.width * Math.sin(turnoverDegree * (Math.PI / 180))) +
        Math.abs(imageSize.height * Math.cos(turnoverDegree * (Math.PI / 180)));
      const { width: containerWidth, height: containerHeight } = containerSize;
      // Rounded down finely (never larger than the view), not to a whole
      // percent: for large images a percent is a lot of screen space
      const fitZoom = (container: number, size: number) =>
        Math.floor((container / size) * 10000) / 10000;
      const scaleWidth = fitZoom(containerWidth, width);
      const scaleHeight = fitZoom(containerHeight, height);
      if (option === 'auto' || option === 'fit-page') {
        return Math.min(scaleWidth, scaleHeight);
      }
      if (option === 'fit-width') {
        return scaleWidth;
      }
      if (option === 'fit-height') {
        return scaleHeight;
      }
      return 1;
    },
    [imageSize, containerSize, turnoverDegree]
  );

  const setZoom = useCallback(
    (v: number | NonNullable<AssetViewerZoomOption>) => {
      if (typeof v === 'number') {
        setZoomFactor(v);
      } else {
        const newZoomFactor = getZoomFactorFromOption(v);
        centerScrollRef.current = true;
        setZoomFactor(newZoomFactor, v);
        // Also when the zoom stays the same
        scrollAfterZoom();
      }
    },
    [getZoomFactorFromOption, setZoomFactor, scrollAfterZoom]
  );

  // Layout effects: zoom and overlay size are applied before the image is
  // painted, otherwise it shows at a stale zoom and then jumps to the fit
  useLayoutEffect(() => {
    setZoomOption('auto');
  }, [isLgWidth, setZoomOption]);

  useLayoutEffect(() => {
    if (!zoomOption) return;
    if (imagePinchGestureRef.current.isActive) return;
    const newZoomFactor = getZoomFactorFromOption(zoomOption);
    setZoomFactor(newZoomFactor, zoomOption);
  }, [zoomOption, getZoomFactorFromOption, setZoomFactor]);

  // While pinching: the image is shown at the zoom being pinched to, not
  // applied (re-rendering) until the pinch ends
  const onPinchPreview = useCallback(
    (zoom: number, focal: { x: number; y: number }) => {
      const image = imageRef.current;
      const content = contentRef.current;
      if (!image || !content) return;
      displayedZoomRef.current = zoom;
      image.style.transform = getImageTransform(
        turnoverDegreeRef.current,
        zoom
      );
      const box = getImageBoxSize(
        imageSizeRef.current,
        turnoverDegreeRef.current,
        zoom
      );
      content.style.width = `${box.width}px`;
      content.style.height = `${box.height}px`;
      zoomFocalRef.current = { ...focal, time: Date.now() };
      scrollAfterZoom();
      syncCanvasLayout();
      rememberScrollState();
    },
    [scrollAfterZoom, syncCanvasLayout, rememberScrollState]
  );

  useMobileImagePinchZoom({
    enabled: !isLgWidth && !isShapeDrawing && !isSelectedCanvasObject,
    viewportRef: containerRef,
    imagePinchGestureRef,
    zoomFactor,
    setZoomFactor,
    onPinchPreview
  });

  // Ctrl + wheel zooms (see PanelAction) at the cursor. Before PanelAction's
  // listener, on the document, which stops the event.
  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      const container = containerRef.current;
      if (!container || !container.contains(e.target as Node)) return;
      zoomFocalRef.current = { x: e.clientX, y: e.clientY, time: Date.now() };
    };
    window.addEventListener('wheel', onWheel, { capture: true, passive: true });
    return () => {
      window.removeEventListener('wheel', onWheel, { capture: true });
    };
  }, []);

  useLayoutEffect(() => {
    displayedZoomRef.current = zoomFactor;
    scrollAfterZoom();
    syncCanvasLayout();
    rememberScrollState();
  }, [
    imageSize,
    zoomFactor,
    turnoverDegree,
    containerSize,
    scrollAfterZoom,
    syncCanvasLayout,
    rememberScrollState
  ]);

  // Dragging the image scrolls it (with the mouse: touch scrolls natively)
  const isPanBlockedRef = useRef(false);
  isPanBlockedRef.current = isShapeDrawing || isSelectedCanvasObject;
  const onMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    const viewport = getScrollViewport();
    if (!viewport) return;
    const start = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: viewport.scrollLeft,
      scrollTop: viewport.scrollTop
    };
    const onMouseMove = (event: MouseEvent) => {
      if (isPanBlockedRef.current) return;
      viewport.scrollLeft = start.scrollLeft - (event.clientX - start.x);
      viewport.scrollTop = start.scrollTop - (event.clientY - start.y);
    };
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const showPanelAction =
    !mobileActionVisibility || mobileActionVisibility.showPanelAction;

  const panelActionConfig = useMemo(
    () => ({
      zoom: zoomFactor,
      zoomOption,
      setZoom,
      isShapeDrawingToolsOpen: isMobileDrawingToolbarOpen,
      isExpand,
      setIsExpand: (v: boolean) => {
        centerScrollRef.current = true;
        setIsExpand(v);
      },
      setRotate: (v: number) => {
        const rt = turnoverDegree + v === 360 ? 0 : turnoverDegree + v;
        centerScrollRef.current = true;
        setTurnoverDegree(rt);
      }
    }),
    [
      zoomFactor,
      zoomOption,
      setZoom,
      isMobileDrawingToolbarOpen,
      isExpand,
      turnoverDegree,
      setTurnoverDegree,
      setIsExpand
    ]
  );

  const onImageLoad = (img: HTMLImageElement) => {
    setImageSize({
      width: img.naturalWidth,
      height: img.naturalHeight
    });
    onReady();
  };

  // Cached images are complete right away: show them before the first paint
  useLayoutEffect(() => {
    const img = imageRef.current;
    if (img?.complete && img.naturalWidth) onImageLoad(img);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Remember what is on screen when navigating away, for the next viewer
  const isAssetReadyRef = useRef(isAssetReady);
  isAssetReadyRef.current = isAssetReady;
  const isPreviousImageShownRef = useRef(isPreviousImageShown);
  isPreviousImageShownRef.current = isPreviousImageShown;
  useLayoutEffect(() => {
    const img = imageRef.current;
    return () => {
      if (img && isAssetReadyRef.current) {
        const { width, height, transform } = img.style;
        setLastShownMedia({
          kind: 'image',
          src: img.currentSrc || img.src,
          style: { width, height, transform },
          hiddenAt: Date.now()
        });
      } else if (previousImageRef.current && isPreviousImageShownRef.current) {
        setLastShownMedia({
          ...previousImageRef.current,
          hiddenAt: Date.now()
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const imageBoxSize = getImageBoxSize(imageSize, turnoverDegree, zoomFactor);
  // Larger than the view (with scrollbars): dragging the image scrolls it
  const isScrollable =
    !!containerSize &&
    (imageBoxSize.width > containerSize.width + 1 ||
      imageBoxSize.height > containerSize.height + 1);

  return (
    <div className="media_viewer_image" ref={containerRef}>
      <OverlayScrollbarsComponent
        className="media_viewer_image__container"
        ref={wrapperRef}
        events={scrollEvents}
      >
        {/* As large as the image is shown, at least the view: scrolls when
            the image is larger */}
        <div
          className="media_viewer_image__content"
          ref={contentRef}
          style={imageBoxSize}
          onMouseDown={onMouseDown}
        >
          {isPreviousImageShown && (
            <img
              src={previousImage.src}
              alt=""
              draggable={false}
              style={{ ...previousImage.style, pointerEvents: 'none' }}
            />
          )}
          <img
            ref={imageRef}
            onLoad={(e) => {
              const img = e.target as HTMLImageElement;
              // Decode first, so swapping from the previous image does not
              // paint an empty frame
              img
                .decode()
                .catch(() => undefined)
                .then(() => onImageLoad(img));
            }}
            src={fileUrl}
            alt="#"
            draggable={false}
            style={{
              ...imageSize,
              transform: getImageTransform(turnoverDegree, zoomFactor),
              backgroundImage: `url("${Transparent}")`,
              // Shown once fully loaded, not painted progressively
              opacity: isAssetReady ? 1 : 0
            }}
          />
          <div ref={frameRef} className="media_viewer_image__frame" />
          <div
            ref={canvasContainerRef}
            className={classNames('media_viewer_image__canvas', {
              'media_viewer_image__canvas--draggable':
                !isShapeDrawing && isScrollable
            })}
          >
            <FabricCanvas />
          </div>
        </div>
      </OverlayScrollbarsComponent>
      {isPreparing ? (
        <PlaybackPreparingLoader />
      ) : (
        !isAssetReady && (
          <PlaybackPreparingLoader delayed delayMs={loaderDelayMs} />
        )
      )}
      {showPanelAction && <PanelAction {...panelActionConfig} />}
    </div>
  );
}

export default ImageCard;
